/**
 * 都市商务模板 - 页脚
 *
 * 设计理念：深藏青 4 栏布局
 * - 深藏青(#14283F) 背景，橙色标题点缀
 * - 第一栏品牌简介（logo + 简介 + 快捷联系），后三栏菜单与联系
 * - 直角风格（rounded-lg），与 Navbar 保持一致
 */

import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function UrbanFooter() {
  const { siteConfig } = useSiteConfig();

  const phone = siteConfig?.contact_info?.phone || '';
  const email = siteConfig?.contact_info?.email || '';
  const address = siteConfig?.contact_info?.address;
  const wechat = siteConfig?.contact_info?.wechat;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || '';
  const icpUrl = siteConfig?.icp_url || '';
  const displayName = siteConfig?.site_name || '';
  const siteName = siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const footerInfo = siteConfig?.footer_info || '';

  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
    { id: "m7", label: "隐私政策", href: "/page/privacy", external: false, enabled: true },
    { id: "m8", label: "服务协议", href: "/page/register-agreement", external: false, enabled: true },
  ];

  const enableVip = siteConfig?.enable_vip ?? true;
  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = (hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus)
    .filter((m: any) => m.href !== '/vip' || enableVip);

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `\u00a9 ${currentYear} ${copyrightOwner}` : `\u00a9 ${currentYear}`;

  const brandName = displayName || siteName;

  const quickMenus = menus.slice(0, 4);
  const moreMenus = menus.slice(4, 8);

  return (
    <footer className="bg-[#14283F] text-gray-400">
      {/* 渐变顶线 */}
      <div className="h-1 bg-gradient-to-r from-[#1B3A5C] via-[#2C5A8C] to-[#F08A24]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* ── 4栏网格 ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* 栏目1: 品牌简介（占2列） */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              {logo ? (
                <img src={logo} alt={brandName} className="h-10 w-10 object-contain rounded-lg" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center text-white text-sm font-black shadow-lg shadow-orange-500/20">
                  {brandName ? brandName.charAt(0) : 'U'}
                </div>
              )}
              {brandName && <span className="text-xl font-bold text-white">{brandName}</span>}
            </div>
            <p className="text-sm leading-relaxed text-gray-400 max-w-md mb-6">
              {footerInfo || '本地分类信息平台，汇聚各类生活服务信息，让生活更便捷。'}
            </p>
            <div className="flex items-center gap-3">
              {phone && (
                <a href={`tel:${phone}`} className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#F08A24] hover:text-white transition-colors" title="电话联系">
                  <i className="ri-phone-line text-lg" />
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#F08A24] hover:text-white transition-colors" title="发送邮件">
                  <i className="ri-mail-line text-lg" />
                </a>
              )}
              {wechat && (
                <span className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#F08A24] hover:text-white transition-colors cursor-default" title="微信">
                  <i className="ri-wechat-line text-lg" />
                </span>
              )}
            </div>
          </div>

          {/* 栏目2: 快捷导航 */}
          <div>
            <h4 className="text-sm font-bold text-white mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-[#F08A24] rounded-sm inline-block" />
              快捷导航
            </h4>
            <ul className="space-y-3">
              {quickMenus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm text-gray-400 hover:text-[#F5A94D] transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm text-gray-400 hover:text-[#F5A94D] transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 栏目3: 更多服务 */}
          <div>
            <h4 className="text-sm font-bold text-white mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-[#F08A24] rounded-sm inline-block" />
              更多服务
            </h4>
            <ul className="space-y-3">
              {moreMenus.length > 0 ? moreMenus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm text-gray-400 hover:text-[#F5A94D] transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm text-gray-400 hover:text-[#F5A94D] transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              )) : (
                <li className="text-sm text-gray-500">暂无更多</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* 底栏 */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{copyright}</span>
          <div className="flex items-center gap-4">
            {icp && (
              <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer"
                className="text-xs text-gray-500 hover:text-[#F5A94D] transition-colors">
                {icp}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
